import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';
import { featureFlags } from '../../config/featureFlags';
import { PM_SERVICE_INTENT_LINKS } from '../../config/pmSeo';
import { getBusinessAddressLines, getBusinessName, getSupportEmail, getSupportPhone } from '../../utils/envUtils';
import { useOptionalSiteSettings } from '../../context/SiteSettingsContext';
import { useOptionalTenantIdentity } from '../../context/TenantIdentityContext';
import { INK, TEXT, MUTED, RULE, GOLD, mono, grotesk } from './tokens';

const DEFAULT_INSTAGRAM_URL = 'https://www.instagram.com/truckparkinghub/';
const DEFAULT_FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61591717839889';

const getContactAddressLines = (address = {}) => {
  const line1 = address.line1 || '';
  const line2 = address.line2 || '';
  const locality = [address.city, address.state].filter(Boolean).join(', ');
  const localityLine = [locality, address.postalCode].filter(Boolean).join(' ');
  const country = address.country || '';
  const countryLabel = ['US', 'USA', 'UNITED STATES'].includes(country.toUpperCase()) ? '' : country;

  return [line1, line2, localityLine, countryLabel].filter(Boolean);
};

const NAV_COLUMNS = [
  ['Truck Parking', [
    ['Find a yard',         '/truck-parking'],
    ['Monthly plans',       '/rates'],
    ['Last-hour parking',   '/truck-parking/last-hour-parking'],
    ['Driver guides',       '/blog'],
    ['I-77 corridor',       '/truck-parking/near-i-77'],
    ['I-85 corridor',       '/truck-parking/near-i-85'],
  ]],
  ['PM Service Guides', PM_SERVICE_INTENT_LINKS.map(({ label, path }) => [label, path])],
  ['Company', [
    ['About',         '/about'],
    ['PM Partners',   '/pm-partners'],
    ['Hiring',        '/hiring'],
    ['My bookings',   '/manage-booking'],
  ]],
  ['Help', [
    ['Contact',             '/contact'],
    ['Terms & Conditions',  '/terms-and-conditions'],
    ['Privacy Policy',      '/privacy-policy'],
    ['Sign in',             '/signin'],
  ]],
];

const getNavColumns = (tenantSessionActive = false) => {
  const visibleColumns = featureFlags.publicHiringEnabled
    ? NAV_COLUMNS
    : NAV_COLUMNS.map(([heading, links]) => [heading, links.filter(([, href]) => href !== '/hiring')]);

  if (!tenantSessionActive) return visibleColumns;
  return visibleColumns.map(([heading, links]) => [
    heading,
    links.map(([label, href]) => (href === '/signin' ? ['Owner workspace', '/tenant'] : [label, href])),
  ]);
};

const InstagramIcon = ({ style }) => (
  <svg style={style} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const FacebookIcon = ({ style }) => (
  <svg style={style} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export default function YardFooter({ onSwitchToLegacy }) {
  const location = useLocation();
  const siteSettings = useOptionalSiteSettings();
  const tenantIdentity = useOptionalTenantIdentity();
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isBookingAccountRoute = /^\/(?:manage-booking|my-bookings)(?:\/|$)/i.test(location.pathname);
  const instagramUrl = siteSettings?.settings?.socialLinks?.instagram || DEFAULT_INSTAGRAM_URL;
  const facebookUrl = siteSettings?.settings?.socialLinks?.facebook || DEFAULT_FACEBOOK_URL;
  const siteBranding = siteSettings?.settings?.branding || {};
  const footerLogoUrl = siteBranding.headerLogoUrl || '/tph-logo-desktop.png';
  const footerLogoAltText = siteBranding.headerLogoAltText || 'Truck Parking Hub';
  const footerBrandPrimaryText = siteBranding.headerBrandPrimaryText || 'Truck Parking';
  const footerBrandSecondaryText = siteBranding.headerBrandSecondaryText || 'Hub';
  const footerBrandName = [footerBrandPrimaryText, footerBrandSecondaryText]
    .filter(Boolean)
    .join(' ')
    .trim() || 'Truck Parking Hub';
  const contactSettings = siteSettings?.settings?.contact || {};
  const settingsAddressLines = getContactAddressLines(contactSettings.address || {});
  const businessName = contactSettings.businessName || getBusinessName();
  const businessAddressLines = settingsAddressLines.length > 0 ? settingsAddressLines : getBusinessAddressLines();
  const supportEmail = contactSettings.supportEmail || getSupportEmail();
  const supportPhone = contactSettings.supportPhone || getSupportPhone();
  const tenantSessionActive = tenantIdentity?.isAuthenticated === true;
  const socialLinks = [
    ['Instagram', instagramUrl, 'instagram_click', InstagramIcon],
    ['Facebook', facebookUrl, 'facebook_click', FacebookIcon],
  ];

  return (
    <footer id="yard-footer" style={{
      padding: isMobile ? '36px 18px' : '48px 40px',
      background: INK,
      borderTop: `1px solid ${RULE}`,
      color: MUTED,
      fontFamily: mono,
      fontSize: 12,
      display: 'flex',
      flexDirection: 'column',
      gap: 36,
    }}>

      {/* Brand row */}
      <div>
        {/* Logo + wordmark + Manage Booking on the same line */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={footerLogoUrl} alt={footerLogoAltText} height={28} style={{ height: 28, flexShrink: 0 }} />
            <div style={{ fontFamily: mono, lineHeight: 1.25 }}>
              <div style={{ color: TEXT, fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 600 }}>
                {footerBrandPrimaryText}
              </div>
              <div style={{ color: MUTED, fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase' }}>
                {footerBrandSecondaryText}
              </div>
            </div>
          </div>

          {!isBookingAccountRoute && (
            <Link
              to="/manage-booking"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                trackEvent('nav_click', { source: 'yard_footer', section: 'manage_booking', path: location.pathname });
              }}
              style={{
                color: GOLD,
                textDecoration: 'none',
                fontFamily: mono,
                fontSize: 12,
                letterSpacing: '.08em',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Manage Booking →
            </Link>
          )}
        </div>

        <p style={{ marginTop: 16, lineHeight: 1.6, maxWidth: 280, color: MUTED, fontFamily: grotesk, fontSize: 13 }}>
          The East Coast's network of truck parking yards. Reserve nightly, weekly, or by the month.
        </p>

        {supportEmail && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, color: MUTED }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            <a href={`mailto:${supportEmail}`} style={{ color: MUTED, textDecoration: 'none', letterSpacing: '.06em' }}>
              {supportEmail}
            </a>
          </div>
        )}
        {supportPhone && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, color: MUTED }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.79.62 2.65a2 2 0 0 1-.45 2.11L8 9.76a16 16 0 0 0 6 6l1.28-1.28a2 2 0 0 1 2.11-.45c.86.29 1.75.5 2.65.62A2 2 0 0 1 22 16.92z" />
            </svg>
            <a href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`} style={{ color: MUTED, textDecoration: 'none', letterSpacing: '.06em' }}>
              {supportPhone}
            </a>
          </div>
        )}
        {businessAddressLines.length > 0 && (
          <address style={{ marginTop: 8, color: MUTED, fontStyle: 'normal', lineHeight: 1.55, letterSpacing: '.04em' }}>
            {businessName}<br />
            {businessAddressLines.map((line) => <React.Fragment key={line}>{line}<br /></React.Fragment>)}
          </address>
        )}
      </div>

      {/* Nav columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, minmax(0, 1fr))',
        gap: 24,
      }}>
        {getNavColumns(tenantSessionActive).map(([heading, links]) => (
          <div key={heading}>
            <div style={{ letterSpacing: '.16em', textTransform: 'uppercase', marginBottom: 14, color: TEXT }}>
              {heading}
            </div>
            {links.map(([label, href]) => (
              <div key={label} style={{ marginBottom: 8 }}>
                <Link
                  to={href}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    trackEvent('nav_click', { source: 'yard_footer', section: label.toLowerCase().replace(/\s+/g, '_'), path: location.pathname });
                  }}
                  style={{ color: MUTED, textDecoration: 'none' }}
                >
                  {label}
                </Link>
              </div>
            ))}
          </div>
        ))}
        <div>
          <div style={{ letterSpacing: '.16em', textTransform: 'uppercase', marginBottom: 14, color: TEXT }}>
            Follow Us
          </div>
          {socialLinks.map(([label, href, eventName, Icon]) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent(eventName, { source: 'yard_footer', path: location.pathname })}
                style={{ color: MUTED, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                title={`Follow us on ${label}`}
              >
                <Icon style={{ width: 14, height: 14, flexShrink: 0 }} />
                <span>{label}</span>
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom strip: copyright only */}
      <div style={{
        borderTop: `1px solid ${RULE}`,
        paddingTop: 18,
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 32px' }}>
          <span style={{ fontSize: 11 }}>
            <span style={{ color: TEXT }}>© {new Date().getFullYear()} {footerBrandName}</span>
            <span style={{ color: MUTED }}> · All rights reserved.</span>
          </span>
        </div>

        {onSwitchToLegacy && (
          <button
            onClick={onSwitchToLegacy}
            style={{
              background: 'none',
              border: `1px solid rgba(255,255,255,.15)`,
              borderRadius: 8,
              padding: '6px 14px',
              fontFamily: mono,
              fontSize: 11,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: MUTED,
              cursor: 'pointer',
            }}
          >
            ← Classic view
          </button>
        )}
      </div>

    </footer>
  );
}
